// src/lib/analytics.js
// Thin GA4 wrapper. gtag.js is loaded from public/index.html with
// send_page_view: false so we control page views manually for the SPA.
//
// PII policy: never send names, emails, phone numbers, or message bodies.
// Only categorical, non-identifying values (service_interest, community,
// villa_slug, cta_location, page_location, page_title).

import { API } from "./api";

const MEASUREMENT_ID = "G-9K7Q3RS8GS";
const META_PIXEL_ID = "2866427143714200";

/**
 * Bootstrap Meta Pixel — official fbq snippet, loaded asynchronously.
 * Idempotent (guards React StrictMode re-invocation) and skipped entirely in
 * headless Chrome (navigator.webdriver) so the prerender/build pipeline never
 * emits fake PageViews. The initial PageView is fired by trackPageView()
 * via RouteAnalytics (which also covers every SPA navigation), guaranteeing
 * exactly ONE PageView per page — no duplicates.
 */
export function initMetaPixel() {
    if (typeof window === "undefined") return;
    if (window.navigator && window.navigator.webdriver) return;
    if (typeof window.fbq === "function" && window.__fbqBooted) return;
    window.__fbqBooted = true;

    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return; n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0";
        n.queue = []; t = b.createElement(e); t.async = !0;
        t.src = v; s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */

    window.fbq("init", META_PIXEL_ID);
}

function hasFbq() {
    return typeof window !== "undefined" && typeof window.fbq === "function";
}

// ---- Meta browser<->server deduplication + Advanced Matching helpers ----
// A single user action shares ONE event_id across the browser Pixel and the
// server CAPI event so Meta counts it exactly once.
export function newEventId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return "evt-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
}

function readCookie(name) {
    if (typeof document === "undefined") return undefined;
    const hit = document.cookie.split("; ").find((c) => c.startsWith(name + "="));
    return hit ? decodeURIComponent(hit.split("=").slice(1).join("=")) : undefined;
}

// First-party _fbp/_fbc cookies improve Event Match Quality on the server side.
export function metaMatchFields() {
    return { fbp: readCookie("_fbp"), fbc: readCookie("_fbc") };
}

// Server-side Meta Contact event for outbound-link clicks (WhatsApp/phone/email).
// keepalive lets the request survive the navigation the click triggers. Carries
// no PII — only a categorical contact_method plus fbp/fbc for matching.
function sendServerContact(contactMethod, eventId) {
    if (typeof window === "undefined") return;
    try {
        fetch(`${API}/track/contact`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            keepalive: true,
            body: JSON.stringify({
                event_id: eventId,
                contact_method: contactMethod,
                event_source_url: window.location.href,
                ...metaMatchFields(),
            }),
        }).catch(() => {});
    } catch (e) {
        /* analytics must never block a user click */
    }
}

/**
 * Fire a Meta Pixel standard event. PII policy identical to GA4: only fixed
 * categorical values (contact_method, service) are ever passed — never names,
 * emails, phones or message contents. When eventId is supplied it is passed as
 * fbq's eventID for browser<->server deduplication.
 */
function fbqTrack(name, params, eventId) {
    if (!hasFbq()) return;
    const opts = eventId ? { eventID: eventId } : undefined;
    if (opts) window.fbq("track", name, params || {}, opts);
    else if (params) window.fbq("track", name, params);
    else window.fbq("track", name);
}

// Consultation-form service interest → Meta Lead `service` param (categorical only).
function metaLeadService(serviceInterest) {
    if (serviceInterest === "Concierge Services") return "concierge";
    return "property_management";
}

/**
 * Bootstrap GA4 — inject gtag.js dynamically. Called once from App.js at
 * mount because Emergent's build pipeline strips inline scripts from
 * public/index.html. Idempotent: safe to call more than once.
 */
export function initAnalytics() {
    if (typeof window === "undefined") return;
    // Robust idempotency: check gtag itself, not just a boolean flag.
    // Under React.StrictMode the flag can be missed across double-invocation.
    if (typeof window.gtag === "function" && window.__gaBooted) return;
    window.__gaBooted = true;

    window.dataLayer = window.dataLayer || [];
    // Standard gtag shim
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", MEASUREMENT_ID, {
        send_page_view: false,
        anonymize_ip: true,
    });

    // Load gtag.js library asynchronously.
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(s);
}

function hasGtag() {
    return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Fire a GA4 page_view. Called by RouteAnalytics on every SPA navigation.
 */
export function trackPageView(path) {
    // Meta Pixel PageView — RouteAnalytics guarantees one call per unique
    // path (initial load, direct entry, and SPA navigations alike).
    if (hasFbq()) window.fbq("track", "PageView");
    if (!hasGtag()) return;
    const url = `${window.location.origin}${path}`;
    window.gtag("event", "page_view", {
        page_location: url,
        page_path: path,
        page_title: document.title,
    });
    // Also update the config so subsequent auto events (enhanced measurement)
    // are attributed to the current URL.
    window.gtag("config", MEASUREMENT_ID, { page_path: path, send_page_view: false });
}

/**
 * Generic event dispatcher — quietly no-ops if gtag isn't ready.
 * Never accept or forward PII fields.
 */
export function trackEvent(name, params = {}) {
    if (!hasGtag()) return;
    const safe = {
        page_location: window.location.href,
        page_title: document.title,
        ...params,
    };
    // Defense in depth: strip any accidentally-passed PII keys.
    ["name", "email", "phone", "message", "first_name", "last_name", "full_name", "address"].forEach((k) => delete safe[k]);
    window.gtag("event", name, safe);
}

// Convenience helpers — one per business event so callsites stay readable.
// Each helper fires the GA4 event AND the matching Meta Pixel event from a
// single place, so components can never double-fire either network. For Lead
// events the caller passes the shared eventId (also sent to the server CAPI via
// the /inquiries payload) so Meta deduplicates browser + server conversions.
export const gaConsultationSubmit = (p, eventId) => {
    trackEvent("consultation_submit", p);
    fbqTrack("Lead", { service: metaLeadService(p && p.service_interest) }, eventId);
};
export const gaRentalInquirySubmit = (p, eventId) => {
    trackEvent("rental_inquiry_submit", p);
    fbqTrack("Lead", { service: "villa_rental" }, eventId);
};
export const gaWhatsappClick = (p) => {
    trackEvent("whatsapp_click", p);
    const eventId = newEventId();
    fbqTrack("Contact", { contact_method: "whatsapp" }, eventId);
    sendServerContact("whatsapp", eventId);
};
export const gaPhoneClick = (p) => {
    trackEvent("phone_click", p);
    const eventId = newEventId();
    fbqTrack("Contact", { contact_method: "phone" }, eventId);
    sendServerContact("phone", eventId);
};
export const gaEmailClick = (p) => {
    trackEvent("email_click", p);
    const eventId = newEventId();
    fbqTrack("Contact", { contact_method: "email" }, eventId);
    sendServerContact("email", eventId);
};
export const gaVillaView = (p) => trackEvent("villa_view", p);
export const gaRentalsCollectionView = () => trackEvent("rentals_collection_view", {});
export const gaRentalPropertyClick = (p) => trackEvent("rental_property_click", p);
export const gaCheckAvailabilityClick = (p) => trackEvent("check_availability_click", p);
export const gaConciergeView = () => trackEvent("concierge_view", {});
export const gaMaintenanceView = () => trackEvent("maintenance_view", {});

export { MEASUREMENT_ID, META_PIXEL_ID };

// Boot analytics at module load so window.gtag/window.fbq are defined before
// any React effect runs. Both guarded internally against double-initialization.
if (typeof window !== "undefined") {
    initAnalytics();
    initMetaPixel();
}
